/**
 * メンバー / チーム単位の平均健康度算出ユーティリティ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 担当中PJ群の健康度スコアを平均し、グレード + 平均値 + 母数を返す（health を持つ案件のみ集計）
 * @graph-connects toGrade [calls] 平均スコアからグレードへ変換
 */

import { toGrade } from '../../../types/project.js';
import type { Project, HealthGrade } from '../../../types/project.js';

export interface AvgHealth {
  /** 平均スコアから導出したグレード（S/A/B/C/D） */
  grade: HealthGrade;
  /** 平均スコア（0-100、四捨五入） */
  score: number;
  /** 平均算出に使った案件数（health を持つ案件のみ） */
  count: number;
}

/**
 * health を持つ案件のみで平均健康度を算出する。
 * 対象案件が無い / いずれも health なしの場合は null。
 * 全体PJ看板のメンバーバッジ / 健康度診断カードと同一口径。
 * @graph-connects none
 */
export function calcAvgHealth(projects: Project[]): AvgHealth | null {
  let total = 0;
  let count = 0;
  for (const p of projects) {
    if (!p.health) continue;
    total += p.health.score;
    count += 1;
  }
  if (count === 0) return null;
  const score = Math.round(total / count);
  return { grade: toGrade(score), score, count };
}

/**
 * 複数メンバーの担当中PJ群をチーム単位でまとめて平均健康度を算出する。
 * 同一案件を複数メンバーが担当していても案件 id で重複排除して 1 件として数える。
 * 対象案件が無い / いずれも health なしの場合は null。
 * @graph-connects calcAvgHealth [calls] 重複排除後の案件群から平均健康度を算出
 */
export function calcTeamAvgHealth(projectLists: Project[][]): AvgHealth | null {
  const byId = new Map<number, Project>();
  for (const list of projectLists) {
    for (const p of list) byId.set(p.id, p);
  }
  return calcAvgHealth([...byId.values()]);
}
